
import React, { useState, useEffect } from 'react';
import { 
  ShieldAlert, 
  MapPin, 
  Car, 
  Search, 
  CheckCircle2, 
  ArrowRight, 
  FileText
} from 'lucide-react';
import Modal from './Modal';
import { Ticket, Vehicle } from '../types';

interface InvestigationModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  vehicle?: Vehicle | null; // Optional context
  onComplete: (conclusion: string, action: 'pay' | 'appeal' | 'transfer') => void;
}

const InvestigationModal: React.FC<InvestigationModalProps> = ({ isOpen, onClose, ticket, vehicle, onComplete }) => {
  // Investigation State
  const [step, setStep] = useState(1);
  const [checks, setChecks] = useState({
    visualMatch: null as boolean | null, // Does photo match car?
    gpsMatch: null as boolean | null,    // Was car at location?
    driverAdmitted: null as boolean | null // Did driver admit fault?
  });
  const [notes, setNotes] = useState('');

  // Derived Recommendation
  const getConclusion = () => {
    if (checks.visualMatch === false) return { action: 'appeal', reason: 'Cloned Vehicle', text: 'The vehicle in the evidence photos does not match our fleet vehicle specifications. This is a strong case for cloning.' };
    if (checks.gpsMatch === false) return { action: 'appeal', reason: 'GPS Evidence', text: 'Telematics data confirms the vehicle was elsewhere at the time of contravention.' };
    if (checks.driverAdmitted === true) return { action: 'transfer', reason: 'Driver Liability', text: 'Driver has admitted possession. Liability should be transferred or recharged.' };
    if (checks.visualMatch === true && checks.gpsMatch === true) return { action: 'pay', reason: 'Valid Contravention', text: 'Evidence confirms the vehicle was present and the contravention likely occurred.' };
    return { action: 'investigate', reason: 'Inconclusive', text: 'Investigation incomplete. Further evidence required.' };
  };

  const conclusion = getConclusion();

  const handleFinish = () => {
    // @ts-ignore
    onComplete(`Investigation Report: ${conclusion.text} \nNotes: ${notes}`, conclusion.action);
    onClose();
  };

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setChecks({ visualMatch: null, gpsMatch: null, driverAdmitted: null });
      setNotes('');
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Internal Investigation"
      maxWidth="max-w-2xl"
    >
      <div className="flex flex-col h-full min-h-[500px]">
        
        {/* Progress Stepper */}
        <div className="flex items-center justify-between px-8 py-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
          {[
            { id: 1, label: 'Visual Check', icon: <Car size={16} /> },
            { id: 2, label: 'Telematics', icon: <MapPin size={16} /> },
            { id: 3, label: 'Driver', icon: <Search size={16} /> },
            { id: 4, label: 'Conclusion', icon: <FileText size={16} /> },
          ].map((s, idx) => (
            <div
              key={s.id}
              className={`flex items-center gap-2 ${
                step === s.id
                  ? 'text-primary-600 dark:text-primary-400 font-bold'
                  : step > s.id
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-slate-400 dark:text-slate-500'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white dark:bg-slate-900 ${
                step === s.id ? 'border-primary-600 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/30' : 
                step > s.id ? 'border-green-600 bg-green-50 dark:border-green-400 dark:bg-green-900/30' : 'border-slate-200 dark:border-slate-700'
              }`}>
                {step > s.id ? <CheckCircle2 size={16} /> : s.icon}
              </div>
              <span className="text-xs hidden sm:inline">{s.label}</span>
              {idx < 3 && <div className="w-8 h-0.5 bg-slate-200 dark:bg-slate-700 mx-2 hidden sm:block" />}
            </div>
          ))}
        </div>

        <div className="p-8 flex-1 overflow-y-auto">
          
          {/* Step 1: Visual Match (Cloning) */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg flex gap-3 border border-blue-100 dark:border-blue-900/40">
                <ShieldAlert className="text-blue-600 dark:text-blue-400 shrink-0" />
                <div>
                  <h4 className="font-bold text-blue-900 dark:text-blue-200">Cloning Check</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-200/90">Verify the vehicle in the PCN photo matches your fleet records.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-lg text-center bg-white dark:bg-slate-950/40">
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-bold mb-2">Fleet Record</p>
                  <p className="text-2xl font-bold text-slate-800 dark:text-white">{ticket.vrn}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{vehicle?.make || 'Unknown'} {vehicle?.model}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{vehicle?.colour || 'Unknown Color'}</p>
                </div>
                <div className="p-4 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950/40">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Review Evidence Photos</p>
                  <a href="#" className="text-primary-600 dark:text-primary-400 text-xs font-bold hover:underline">View Evidence Tab</a>
                </div>
              </div>

              <div className="space-y-3">
                <p className="font-medium text-slate-900 dark:text-white">Does the vehicle in the photo match yours?</p>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => { setChecks({...checks, visualMatch: true}); setStep(2); }}
                    className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-700 text-left transition-all"
                  >
                    <span className="font-bold text-slate-800 dark:text-white block mb-1">Yes, it matches</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">Same make, model, and color.</span>
                  </button>
                  <button 
                    onClick={() => { setChecks({...checks, visualMatch: false}); setStep(4); }} // Skip to end if cloned
                    className="p-4 border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-left transition-all"
                  >
                    <span className="font-bold text-red-800 dark:text-red-200 block mb-1">No, it looks different</span>
                    <span className="text-xs text-red-600 dark:text-red-200/80">Possible cloned vehicle.</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Telematics (GPS) */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg flex gap-3 border border-purple-100 dark:border-purple-900/40">
                <MapPin className="text-purple-600 dark:text-purple-400 shrink-0" />
                <div>
                  <h4 className="font-bold text-purple-900 dark:text-purple-200">Location Verification</h4>
                  <p className="text-sm text-purple-700 dark:text-purple-200/90">Check your telematics/GPS provider for {new Date(ticket.contravention_date || ticket.issue_date).toLocaleDateString('en-GB')}.</p>
                </div>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-950/40 rounded-lg border border-slate-200 dark:border-slate-800">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Contravention Time</span>
                  <span className="text-sm font-mono font-bold text-slate-900 dark:text-slate-100">{new Date(ticket.contravention_date || ticket.issue_date).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Contravention Location</span>
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{ticket.location}</span>
                </div>
              </div>

              <div className="space-y-3">
                <p className="font-medium text-slate-900 dark:text-white">Was the vehicle at this location at this time?</p>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => { setChecks({...checks, gpsMatch: true}); setStep(3); }}
                    className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-700 text-left transition-all"
                  >
                    <span className="font-bold text-slate-800 dark:text-white block mb-1">Yes, GPS confirms</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">Vehicle was present.</span>
                  </button>
                  <button 
                    onClick={() => { setChecks({...checks, gpsMatch: false}); setStep(4); }} // Skip to end if GPS proves alibi
                    className="p-4 border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-left transition-all"
                  >
                    <span className="font-bold text-red-800 dark:text-red-200 block mb-1">No, GPS shows elsewhere</span>
                    <span className="text-xs text-red-600 dark:text-red-200/80">Strong grounds for appeal.</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Driver */}
          {step === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg flex gap-3 border border-amber-100 dark:border-amber-900/40">
                <Search className="text-amber-600 dark:text-amber-400 shrink-0" />
                <div>
                  <h4 className="font-bold text-amber-900 dark:text-amber-200">Driver Check</h4>
                  <p className="text-sm text-amber-700 dark:text-amber-200/90">Establish who was in control of the vehicle.</p>
                </div>
              </div>

              <div className="space-y-3">
                <p className="font-medium text-slate-900 dark:text-white">Have you identified the driver?</p>
                <div className="grid grid-cols-1 gap-3">
                  <button 
                    onClick={() => { setChecks({...checks, driverAdmitted: true}); setStep(4); }}
                    className="p-4 border border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-left transition-all rounded-xl"
                  >
                    <span className="font-bold text-green-800 dark:text-green-200 block mb-1">Yes, Driver Assigned / Admitted</span>
                    <span className="text-xs text-green-600 dark:text-green-200/80">Liability can be transferred.</span>
                  </button>
                  <button 
                    onClick={() => { setChecks({...checks, driverAdmitted: false}); setStep(4); }}
                    className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-700 text-left transition-all"
                  >
                    <span className="font-bold text-slate-800 dark:text-white block mb-1">No / Unsure</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">We might be liable as the keeper.</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Conclusion */}
          {step === 4 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
              
              <div className={`p-6 rounded-xl border flex gap-4 ${
                conclusion.action === 'appeal'
                  ? 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-900/40'
                  : conclusion.action === 'transfer'
                    ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-900/40'
                    : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-900/40'
              }`}>
                <div className={`p-3 rounded-full h-fit ${
                   conclusion.action === 'appeal'
                     ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300'
                     : conclusion.action === 'transfer'
                       ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-300'
                       : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300'
                }`}>
                  <FileText size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-1">Recommended Action: {conclusion.action.toUpperCase()}</h3>
                  <p className="font-medium text-slate-700 dark:text-slate-200 mb-2">Reason: {conclusion.reason}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{conclusion.text}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Investigation Notes</label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full p-3 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  rows={3}
                  placeholder="Add any specific details (e.g. 'Driver stated they were unloading')..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button 
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg font-medium"
                >
                  Restart
                </button>
                <button 
                  onClick={handleFinish}
                  className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg font-bold hover:bg-slate-800 dark:hover:bg-slate-200 flex items-center gap-2 transition-colors"
                >
                  Save Report & Proceed to {conclusion.action.charAt(0).toUpperCase() + conclusion.action.slice(1)} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </Modal>
  );
};

export default InvestigationModal;
